import {useCallback, useEffect, useMemo, useState} from "react";
import {useEventHandlers} from "../../SigmaEdgeGraphLoader";

const DEFAULT_SETTINGS = {
  onSelectionStart: null,
  onSelectionChange: null,
  onSelectionEnd: null,
  disabled: null,
};

// Function used to check collision between a selection rectangle and the nodes as circles
// Circle = {x, y, r}
// Rectangle = {x, y, w, h}
const checkCollision = (circle, rect) => {
  const distX = Math.abs(circle.x - rect.x - rect.w / 2);
  const distY = Math.abs(circle.y - rect.y - rect.h / 2);

  if (distX > rect.w / 2 + circle.r) return false;
  if (distY > rect.h / 2 + circle.r) return false;

  if (distX <= rect.w / 2) return true;
  if (distY <= rect.h / 2) return true;

  const dx = distX - rect.w / 2;
  const dy = distY - rect.h / 2;

  return dx * dx + dy * dy <= circle.r * circle.r;
};

// The plugin function "enhancing" the given renderer
const useSelectionRectangle = (sigma, container, settings = {}) => {
  const {onSelectionStart, onSelectionChange, onSelectionEnd, disabled} = {...DEFAULT_SETTINGS, ...(settings || {})};

  const graph = sigma.getGraph();
  const camera = sigma.camera;
  const [maxNodeSize, setMaxNodeSize] = useState(-Infinity);
  const [selectionDiv, setSelectionDiv] = useState(null);
  const state = useMemo(() => ({
    isSelecting: false,
    xStart: 0,
    yStart: 0,
    xCurrent: 0,
    yCurrent: 0
  }), []);

  class SelectionState {
    get isActive() { return state.isSelecting; }
    get bounds() { return getSelectionRectangle(); }
    get selectedNodes() { return getSelectedNodes(); }
  }
  const selectionState = useMemo(() => new SelectionState(), [state]);

  useEffect(() => {
    const handlers = {
      selectionStart: onSelectionStart,
      selectionChange: onSelectionChange,
      selectionEnd: onSelectionEnd,
    }
    const handleKeyDown = e => {
      if (e.key === "Escape" && state.isSelecting) {
        camera.enable();
        state.isSelecting = false;
        if (selectionDiv) {
          selectionDiv.style.display = 'none';
        }
        sigma.emit('selectionEnd', {state: selectionState, nodes: null, canceled: true});
        e.stopPropagation();
        e.preventDefault();
      }
    };
    Object.entries(handlers).forEach(([event, handler]) => handler && sigma?.on(event, handler));
    const body = window.document.body;
    body.addEventListener("keydown", handleKeyDown);
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => handler && sigma?.off(event, handler));
      body.removeEventListener("keydown", handleKeyDown);
    };
  }, [sigma, container, selectionDiv, onSelectionStart, onSelectionChange, onSelectionEnd]);

  useEffect(() => {
    let max = 0;
    graph.forEachNode((node, attr) => {
      max = Math.max(attr.size, max);
    });
    setMaxNodeSize(max);
  }, [graph]);

  useEffect(() => {
    const div = document.createElement('div');
    div.className = "selection-rectangle";
    div.style.display = 'none';
    container.appendChild(div);
    setSelectionDiv(div);
    return () => {
      setSelectionDiv(null);
      container.removeChild(div);
    }
  }, [container]);

  const getSelectionRectangle = () => {
    const x1 = state.xStart;
    const y1 = state.yStart;

    const x2 = state.xCurrent;
    const y2 = state.yCurrent;

    const x3 = Math.min(x1, x2);
    const y3 = Math.min(y1, y2);

    const x4 = Math.max(x1, x2);
    const y4 = Math.max(y1, y2);

    return {
      x1: x1,
      y1: y1,
      x2: x2,
      y2: y2,
      x: x3,
      y: y3,
      width: x4 - x3,
      height: y4 - y3
    };
  };

  const updateSelectionElement = (element = null, displayStyle = null) => {
    if (element) {
      if (displayStyle) {
        element.style.display = displayStyle;
      }
      const rectangle = getSelectionRectangle();
      element.style.left = `${rectangle.x}px`;
      element.style.top = `${rectangle.y}px`;
      element.style.width = `${rectangle.width}px`;
      element.style.height = `${rectangle.height}px`;
    }
  };

  const offset = (el) => {
    const rect = el.getBoundingClientRect();

    return {
      top: rect.top + document.documentElement.scrollTop,
      left: rect.left + document.documentElement.scrollLeft
    };
  };

  const getEventX = (event) => {
    if (event.original) event = event.original;

    return event.pageX - offset(container).left;
  };

  const getEventY = (event) => {
    if (event.original) event = event.original;

    return event.pageY - offset(container).top;
  };

  const sizeRatio = Math.pow(camera.getState().ratio, 0.5); // NOTE: actual rendered size is divided by ratio

  const getSelectedNodes = (rectangle = null) => {
    if (rectangle === null) {
      rectangle = getSelectionRectangle();
    }

    // First pass: find all nodes that intersect the selection rectangle spatially
    const collisionRectangle = {
      x: rectangle.x,
      y: rectangle.y,
      w: rectangle.width,
      h: rectangle.height
    };

    // Get ALL nodes that spatially collide with the drag rectangle
    // Don't filter by hidden attribute - just use spatial collision
    const spatiallySelectedNodes = graph.nodes()
      .filter(nodeID => {
        const attr = graph.getNodeAttributes(nodeID);
        const nodePosition = sigma.graphToViewport({x: attr.x, y: attr.y});
        const size = attr.size / sizeRatio;
        const collisionCircle = {
          r: size,
          x: nodePosition.x,
          y: nodePosition.y
        };
        return checkCollision(collisionCircle, collisionRectangle);
      });

    console.log('[SelectionRectangle] Spatially selected:', spatiallySelectedNodes.length, 'nodes');

    if (spatiallySelectedNodes.length === 0) {
      return [];
    }

    // Group selected nodes by column
    const nodesByColumn = new Map();
    spatiallySelectedNodes.forEach(nodeID => {
      const attr = graph.getNodeAttributes(nodeID);
      const column = attr.column;
      if (!nodesByColumn.has(column)) {
        nodesByColumn.set(column, []);
      }
      nodesByColumn.get(column).push({ id: nodeID, ...attr });
    });

    // CRITICAL: Drag-select should only affect ONE column at a time
    // Choose the column with the most selected nodes (or first if tied)
    let targetColumn = -1;
    let maxNodesInColumn = 0;
    nodesByColumn.forEach((selectedInColumn, column) => {
      if (selectedInColumn.length > maxNodesInColumn) {
        maxNodesInColumn = selectedInColumn.length;
        targetColumn = column;
      }
    });

    if (targetColumn === -1) {
      return [];
    }

    // Return only the spatially-selected nodes (no expansion)
    // When there are hidden nodes interspersed (e.g., disconnected targets),
    // expanding to "contiguous" array indices would incorrectly include them
    console.log('[SelectionRectangle] Returning', spatiallySelectedNodes.length, 'node IDs:',
                spatiallySelectedNodes.slice(0, 5).map(id => id.substring(0, 20)));
    return spatiallySelectedNodes;
  };

  const handleDownStage = useCallback(({event})=> {
    // Don't allow selection to start when disabled
    if (disabled?.current) return;

    camera.disable();
    state.isSelecting = true;
    state.xStart = getEventX(event);
    state.yStart = getEventY(event);
    state.xCurrent = state.xStart;
    state.yCurrent = state.yStart;

    sigma.emit('selectionStart', {state: selectionState});
  }, [sigma, camera, selectionDiv]);

  const handleMouseUp = useCallback(() => {
    if (!state.isSelecting) return;

    camera.enable();
    state.isSelecting = false;
    if (selectionDiv) {
      selectionDiv.style.display = 'none';
    }

    sigma.emit('selectionEnd', {state: selectionState, nodes: getSelectedNodes()});
    // TODO: suppress click event? or have click handler ignore drag?

  }, [sigma, camera, selectionDiv, getSelectedNodes]);

  const handleMouseMove = useCallback((event) => {
    if (!state.isSelecting) return;

    // Get container bounds
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Clamp coordinates to container bounds to prevent selection rectangle from disappearing
    state.xCurrent = Math.max(0, Math.min(containerWidth, getEventX(event)));
    state.yCurrent = Math.max(0, Math.min(containerHeight, getEventY(event)));

    updateSelectionElement(selectionDiv, 'block');

    sigma.emit('selectionChange', {state: selectionState, nodes: getSelectedNodes()});

  }, [sigma, selectionDiv, getSelectedNodes, container]);

  const handleDocumentMouseDown = useCallback((event) => {
    // Ignore events from UI controls (inputs, buttons, selects, etc.)
    const targetElement = event.target;
    const isUIControl = targetElement.tagName === 'INPUT'
                        || targetElement.tagName === 'BUTTON'
                        || targetElement.tagName === 'SELECT'
                        || targetElement.tagName === 'TEXTAREA'
                        || targetElement.tagName === 'A'
                        || targetElement.closest('.label-spacing-control')
                        || targetElement.closest('.graph-controls-right')
                        || targetElement.closest('.graph-controls-left')
                        || targetElement.closest('.graph-controls')
                        || targetElement.closest('button')
                        || targetElement.closest('input');

    if (isUIControl) {
      return; // Don't interfere with UI controls
    }

    // Check if mousedown is in the gutter area (between window edge and container)
    const containerRect = container.getBoundingClientRect();
    const clickX = event.clientX;
    const clickY = event.clientY;

    // Check if click is in the gutter (left/top/right/bottom of container)
    const inHorizontalGutter = clickY >= containerRect.top && clickY <= containerRect.bottom;
    const inVerticalGutter = clickX >= containerRect.left && clickX <= containerRect.right;
    const inLeftGutter = clickX < containerRect.left && inHorizontalGutter;
    const inTopGutter = clickY < containerRect.top && inVerticalGutter;

    if (inLeftGutter || inTopGutter) {
      // Start selection from gutter
      camera.disable();
      state.isSelecting = true;
      // Clamp start position to container edge
      state.xStart = Math.max(0, clickX - containerRect.left);
      state.yStart = Math.max(0, clickY - containerRect.top);
      state.xCurrent = state.xStart;
      state.yCurrent = state.yStart;

      sigma.emit('selectionStart', {state: selectionState});
      event.preventDefault();
      event.stopPropagation();
    }
  }, [sigma, camera, container, selectionState]);

  useEventHandlers({
                     downStage: handleDownStage,
                   });

  // Attach global mouse event listeners to track outside container
  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleDocumentMouseDown, handleMouseMove, handleMouseUp]);

  return selectionState;
}

export default useSelectionRectangle;
